/**
 * Client API pour le backend PHP cil-sablettes.
 * - Préfixe automatique avec VITE_API_BASE_URL (fallback prod : https://api.cil-sablettes.org)
 * - Attache le Bearer token JWT depuis sessionStorage
 * - Gère les réponses JSON:API (extraction de data/attributes) et JSON simple
 * - Lève ApiError sur status >= 400
 */

// L'API PHP est servie sur le même domaine que le front en prod
// (ex: https://intranet.cil-sablettes.org/api/...).
// VITE_API_BASE_URL peut surcharger cette valeur en dev.
// Sur les domaines Lovable (preview/sandbox), on bascule automatiquement
// vers l'API en prod pour que les appels fonctionnent.
const ENV_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/+$/, "");
const PROD_API = "https://intranet.cil-sablettes.org";

function detectBaseUrl(): string {
  if (ENV_BASE) return ENV_BASE;
  if (typeof window === "undefined") return PROD_API;
  const host = window.location.hostname;
  // Domaines Lovable → utiliser l'API prod (CORS doit l'autoriser)
  if (
    host.endsWith(".lovable.app") ||
    host.endsWith(".lovableproject.com") ||
    host.endsWith(".sandbox.lovable.dev")
  ) {
    return PROD_API;
  }
  return window.location.origin;
}

const BASE_URL = detectBaseUrl();

const TOKEN_KEY = "cil.jwt";

/**
 * Décode la partie payload d'un JWT sans vérifier la signature.
 * Retourne null si le token est invalide.
 */
export function decodeJwtPayload(token: string): { exp?: number; sub?: string; email?: string } | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const json = typeof atob === "function" ? atob(padded) : Buffer.from(padded, "base64").toString("utf-8");
    return JSON.parse(json);
  } catch {
    return null;
  }
}

/**
 * Vérifie si un JWT est expiré (avec marge de 5 secondes).
 */
export function isJwtExpired(token: string): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.exp !== "number") return true;
  return payload.exp * 1000 <= Date.now() + 5000;
}

/**
 * Retourne le timestamp d'expiration en ms, ou null si invalide.
 */
export function getJwtExpiryMs(token: string): number | null {
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.exp !== "number") return null;
  return payload.exp * 1000;
}

/** Circuit-breaker : une fois "tripped", toutes les requêtes authentifiées sont court-circuitées. */
let authCircuitTripped = false;
type AuthExpiredReason = "expired" | "unauthorized";
type AuthExpiredHandler = (reason: AuthExpiredReason) => void;
const authExpiredHandlers = new Set<AuthExpiredHandler>();

export function onAuthExpired(handler: AuthExpiredHandler): () => void {
  authExpiredHandlers.add(handler);
  return () => authExpiredHandlers.delete(handler);
}

function tripAuthCircuit(reason: AuthExpiredReason) {
  if (authCircuitTripped) return;
  authCircuitTripped = true;
  tokenStore.clear();
  for (const h of authExpiredHandlers) {
    try { h(reason); } catch { /* noop */ }
  }
}

export function resetAuthCircuit() {
  authCircuitTripped = false;
}

export function isAuthCircuitTripped(): boolean {
  return authCircuitTripped;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public detail?: string,
    public payload?: unknown
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export const tokenStore = {
  get(): string | null {
    if (typeof window === "undefined") return null;
    return window.sessionStorage.getItem(TOKEN_KEY);
  },
  set(token: string) {
    if (typeof window === "undefined") return;
    window.sessionStorage.setItem(TOKEN_KEY, token);
  },
  clear() {
    if (typeof window === "undefined") return;
    window.sessionStorage.removeItem(TOKEN_KEY);
  },
};

interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  /** Si true, n'envoie pas le Bearer token (ex: /auth/login) */
  noAuth?: boolean;
  /** Si true, retourne la réponse brute sans tenter de parser JSON:API */
  rawJson?: boolean;
  signal?: AbortSignal;
}

function buildUrl(path: string, query?: RequestOptions["query"]): string {
  const url = new URL(path.startsWith("/") ? `${BASE_URL}${path}` : `${BASE_URL}/${path}`);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null && v !== "") {
        url.searchParams.set(k, String(v));
      }
    }
  }
  return url.toString();
}

/**
 * Aplati une ressource JSON:API { type, id, attributes } en { id, ...attributes }.
 * Renvoie tel quel si ce n'est pas du JSON:API.
 */
function flattenResource<T = Record<string, unknown>>(res: unknown): T {
  if (!res || typeof res !== "object") return res as T;
  const r = res as Record<string, unknown>;
  if ("attributes" in r && typeof r.attributes === "object" && r.attributes !== null) {
    return { id: r.id, ...(r.attributes as Record<string, unknown>) } as T;
  }
  return res as T;
}

/**
 * Normalise une réponse :
 * - { data: [...] } JSON:API → tableau aplati
 * - { data: {...} } JSON:API → objet aplati
 * - sinon : retourne tel quel
 */
export function unwrap<T = unknown>(payload: unknown): T {
  if (!payload || typeof payload !== "object") return payload as T;
  const p = payload as Record<string, unknown>;
  if ("data" in p) {
    const data = p.data;
    if (Array.isArray(data)) return data.map((x) => flattenResource(x)) as T;
    return flattenResource(data) as T;
  }
  return payload as T;
}

export async function apiRequest<T = unknown>(
  path: string,
  opts: RequestOptions = {}
): Promise<T> {
  const { method = "GET", body, query, noAuth, rawJson, signal } = opts;
  const headers: Record<string, string> = { Accept: "application/json" };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (!noAuth) {
    // Court-circuit : si l'auth a déjà échoué, on bloque toutes les requêtes
    // authentifiées pour éviter une rafale de 401 (et un blocage IP par LWS).
    if (authCircuitTripped) {
      throw new ApiError(401, "Session expirée");
    }
    const t = tokenStore.get();
    if (t) {
      // Vérification locale de l'expiration : on évite l'aller-retour réseau.
      if (isJwtExpired(t)) {
        tripAuthCircuit("expired");
        throw new ApiError(401, "Session expirée");
      }
      headers.Authorization = `Bearer ${t}`;
      headers["X-Authorization"] = `Bearer ${t}`;
      headers["X-CIL-JWT"] = t;
    }
  }

  let res: Response;
  try {
    res = await fetch(buildUrl(path, query), {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal,
      cache: method === "GET" ? "no-store" : "default",
    });
  } catch (e) {
    throw new ApiError(0, "Erreur réseau", (e as Error)?.message);
  }

  // 204 No Content
  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let parsed: unknown = undefined;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (!res.ok) {
    // Sur 401 d'une requête authentifiée → trip du circuit-breaker pour
    // empêcher d'autres appels d'arriver et de saturer le quota LWS.
    if (res.status === 401 && !noAuth) {
      tripAuthCircuit("unauthorized");
    }
    const p = (parsed ?? {}) as Record<string, unknown>;
    const message =
      (typeof p.error === "string" && p.error) ||
      (typeof p.message === "string" && p.message) ||
      (Array.isArray(p.errors) && p.errors[0] && typeof (p.errors[0] as Record<string, unknown>).title === "string"
        ? (p.errors[0] as Record<string, string>).title
        : null) ||
      `Erreur HTTP ${res.status}`;
    const detail =
      (Array.isArray(p.errors) && p.errors[0] && typeof (p.errors[0] as Record<string, unknown>).detail === "string"
        ? (p.errors[0] as Record<string, string>).detail
        : undefined) || (typeof p.detail === "string" ? (p.detail as string) : undefined);
    throw new ApiError(res.status, message, detail, parsed);
  }

  if (rawJson) return parsed as T;
  return unwrap<T>(parsed);
}

export const api = {
  get: <T = unknown>(path: string, opts?: Omit<RequestOptions, "method" | "body">) =>
    apiRequest<T>(path, { ...opts, method: "GET" }),
  post: <T = unknown>(path: string, body?: unknown, opts?: Omit<RequestOptions, "method" | "body">) =>
    apiRequest<T>(path, { ...opts, method: "POST", body }),
  patch: <T = unknown>(path: string, body?: unknown, opts?: Omit<RequestOptions, "method" | "body">) =>
    apiRequest<T>(path, { ...opts, method: "PATCH", body }),
  put: <T = unknown>(path: string, body?: unknown, opts?: Omit<RequestOptions, "method" | "body">) =>
    apiRequest<T>(path, { ...opts, method: "PUT", body }),
  delete: <T = unknown>(path: string, opts?: Omit<RequestOptions, "method" | "body">) =>
    apiRequest<T>(path, { ...opts, method: "DELETE" }),
};

export const API_BASE_URL = BASE_URL;
